import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import styles from "../../../styles/patient-page-modules/orderDeviceModal.module.css";
import Spinner from "../../spinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTicket,
  getAssignedDevices,
  getOrderedDevices,
  getDropshipedDevices,
  getTickets,
} from "../../../services/patient/devices";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import toast from "react-hot-toast";
import Button from "../../button-v1";

const schema = {
  assigned_device_id: "",
  ordered_device_id: "",
  issue_description: "",
};

const validationSchema = z
  .object({
    assigned_device_id: z.number().optional(),
    ordered_device_id: z.number().optional(),
    issue_description: z.string(),
  })
  .refine((data) => data.assigned_device_id || data.ordered_device_id, {
    message: "Either assigned or ordered device is required",
    path: ["assigned_device_id"], // You can point to either key or use a custom one
  });
const OrderDeviceModal = ({ isOpen, onClose }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: schema,
    // resolver: zodResolver(validationSchema),
  });

  const {
    data: patient,
    error,
    isLoading: isPatientLoading,
    refetch,
  } = useQuery(["patient-general-details"], () => getPatientGeneralDetails());

  const { data: assignedDevices, isLoading: isAssignedDevicesLoading } =
    useQuery(["patient-assigned-devices", patientId], () =>
      getAssignedDevices()
    );

  const { data: orderedDevices, isLoading: isOrderedDevicesLoading } = useQuery(
    ["patient-ordered-devices", patientId, locationId],
    () => getDropshipedDevices({ locationId, patientId }),
    {
      select: (data) => {
        return data.orders;
      },
    }
  );

  const {
    mutate: createTicketMMutation,
    isLoading: isCreateTickerMutationLoading,
  } = useMutation((data) => createTicket(data), {
    onSuccess: () => {
      toast.success("Support Ticket add successfully");
      queryClient.invalidateQueries(["patient-technical-support-tickets"]);
      onClose();
    },
    onError: (error) => {
      toast.error(error.response.data?.error || "Could not add support ticket");
    },
  });

  const submitHandler = (values) => {
    if (ordered_device_id) {
      delete values.assigned_device_id;
    } else {
      delete values.ordered_device_id;
    }

    values.patient_id = patientId;

    createTicketMMutation(values);
  };

  const ordered_device_id = watch("ordered_device_id");
  const assigned_device_id = watch("assigned_device_id");

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Support Request</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className={styles.request_header}>
            <div className={styles.info}>
              <h4>Patient Information</h4>
              <div className={styles.details}>
                <div>
                  <h5>Name</h5>
                  <p>
                    {patient?.user?.first_name} {patient?.user?.last_name}
                  </p>
                </div>
                <div>
                  <h5>Time Zone</h5>
                  <p>UTC +5</p>
                </div>
                <div>
                  <h5>Email</h5>
                  <p>{patient?.user?.email}</p>
                </div>
                <div>
                  <h5>Phone</h5>
                  <p>{patient?.user?.primary_phone}</p>
                </div>
              </div>
            </div>

            <div className={styles.info}>
              <h4>Device Information</h4>
              <div className={styles.details}>
                <div>
                  <h5>Gateway ID / IMEI</h5>
                  <p>
                    {
                      orderedDevices?.find(
                        (device) => device.id === ordered_device_id
                      )?.imei
                    }
                    {
                      assignedDevices?.find(
                        (device) => device.id === assigned_device_id
                      )?.imei
                    }
                  </p>
                </div>
                <div>
                  <h5>Device Type</h5>
                  <p>
                    {
                      orderedDevices?.find(
                        (device) => device.id === ordered_device_id
                      )?.name
                    }
                    {
                      assignedDevices?.find(
                        (device) => device.id === assigned_device_id
                      )?.name
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {isOrderedDevicesLoading || isAssignedDevicesLoading ? (
            <p className="content-center">
              <Spinner />
            </p>
          ) : (
            <form className={styles.input_wrapper}>
              <small>Select device to open technical support request</small>
              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Ordered Devices</label>
                  <Select
                    onChange={(value) => setValue("ordered_device_id", value)}
                    value={ordered_device_id}
                    disabled={getValues("assigned_device_id") ? true : false}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select device">
                        <p>
                          {orderedDevices?.find(
                            (_od) => _od.id === ordered_device_id
                          )?.name || "Select device"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {orderedDevices?.map((device, index) => (
                        <SelectItem key={device?.id} value={device?.id}>
                          {device?.name} <small>({device.order_number})</small>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="error-message">
                    {errors?.ordered_device_id?.message}
                  </span>
                </div>
                <div className={styles.input_field}>
                  <label>Assigned Devices</label>
                  <Select
                    onChange={(value) => setValue("assigned_device_id", value)}
                    value={assigned_device_id}
                    disabled={getValues("ordered_device_id") ? true : false}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select device">
                        <p>
                          {assignedDevices?.find(
                            (_ad) => _ad.id === assigned_device_id
                          )?.name || "Select device"}
                        </p>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {assignedDevices?.map((device, index) => (
                        <SelectItem key={device.id} value={device.id}>
                          {device.name} <small>({device.imei})</small>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="error-message">
                    {errors?.assigned_device_id?.message}
                  </span>
                </div>
              </div>

              <div className={styles.input_group}>
                <div className={styles.input_field}>
                  <label>Issue Description</label>
                  <textarea
                    type="text"
                    name="notess"
                    {...register("issue_description")}
                  />
                  <span className="error-message">
                    {errors?.issue_description?.message}
                  </span>
                </div>
                {/* <div className={styles.input_field}></div> */}
              </div>
            </form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button variant="ghost" onClick={() => reset()}>
            Reset
          </Button>
          <Button
            onClick={handleSubmit(submitHandler)}
            loading={isCreateTickerMutationLoading}
            disabled={isCreateTickerMutationLoading}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDeviceModal;
